import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase/client';
import AppointmentScheduler from './AppointmentScheduler';
import RescheduleForm from './RescheduleForm';
import WhatsappConfirmButton from './WhatsappConfirmButton';
import { useConsultationActions } from '../../hooks/useConsultationActions';
import { useConfirm } from '../ui/ConfirmProvider';
import { useToast } from '../ui/ToastProvider';
import ModalShell from '../ui/ModalShell';
import { Skeleton } from '../ui/Skeleton';
import { EmailService } from '../../services/email.service';
import { toSantiagoDateStr, toSantiagoTime } from '../../lib/utils/dateUtils';
import type { AgendaPeriod } from '../../lib/agendaExport';
import './TherapistCalendar.css';

interface Appointment {
  id: string;
  patient_name: string;
  patient_email?: string;
  patient_phone?: string;
  therapy_type: 'individual' | 'pareja' | 'familia';
  start_time: string;
  end_time: string;
  date: string;
  status: 'agendada' | 'programada' | 'confirmada' | 'en_progreso' | 'completada' | 'cancelada' | 'no_asistio';
  modality: 'presencial' | 'online' | 'domicilio';
  duration: number;
  notes?: string;
  patient_id?: string;
  precio?: number;
  meet_link?: string;
  // Fase 0.5 — intención de pago declarada por el paciente al agendar, no un
  // pago real. Campo agregado en sql/migrations/metodo_pago_declarado_booking_2026_07_15.sql
  // (todavía no aplicado a producción); database.types.ts no lo incluye aún.
  paymentMethod: 'efectivo' | 'transferencia' | 'tarjeta';
}

interface SupabaseConsulta {
  id: string;
  fecha_hora: string;
  duracion: number;
  modalidad: 'presencial' | 'online';
  estado: 'agendada' | 'programada' | 'confirmada' | 'en_progreso' | 'completada' | 'cancelada' | 'no_asistio';
  tipo_consulta: string;
  valor_consulta?: number;
  notas_terapeuta?: string;
  meet_link?: string;
  metodo_pago_declarado?: 'efectivo' | 'transferencia' | 'tarjeta';
  pacientes: {
    nombre: string;
    apellido: string;
    email?: string;
    telefono?: string;
  }[];
}

interface TherapistCalendarProps {
  therapist: {
    id: string;
    name: string;
    email?: string;
    // Ya vienen en runtime desde TherapistPortal.tsx (compatibleTherapist es
    // un spread de AuthenticatedTherapist) — se tipan acá como opcionales
    // solo para el membrete de la agenda exportada, no se usaban antes.
    nombre?: string;
    apellido?: string;
    rut?: string;
    titulo?: string;
    especialidad?: string;
    telefono?: string;
  };
}

type CalendarView = 'day' | 'week' | 'month';

// FUNC-131: 'tarjeta' = Débito/Crédito; 'efectivo' queda como legado (agendas previas al cambio).
const paymentMethodLabel = (m: Appointment['paymentMethod']): string =>
  m === 'tarjeta' ? 'Débito / Crédito' : m === 'transferencia' ? 'Transferencia / Efectivo' : 'En la clínica (efectivo o tarjeta)';

const toLocalDateStr = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

// ─── Escala temporal unificada ───────────────────────────────────────────────
// ROW_H = píxeles por hora → PPM = píxeles por minuto = 1
// top = (startMinutes - BASE_MINUTES) * PPM
// height = durationMinutes * PPM
// Las líneas del grid, los eventos y handleGridClick usan EXACTAMENTE la misma fórmula.
// 60 px/hora (antes 120 → 1472 px de grilla, la semana obligaba a scrollear la
// página entera). Paridad con ClinicCalendar (UI-026).
const ROW_H        = 60;           // px/hora
const PPM          = ROW_H / 60;   // 1 px/min
const START_HOUR   = 9;
const END_HOUR     = 20;
const HOURS        = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => START_HOUR + i);
const GRID_PADDING = 8;                                     // aire visual al cierre de jornada
const GRID_HEIGHT  = (END_HOUR - START_HOUR + 1) * ROW_H + GRID_PADDING;  // 728 px
// Bajo esta altura la cita se pinta en una sola línea (hora + nombre)
const TIGHT_EVENT_H = 38;

// "5 – 11 oct" o "29 sept – 5 oct" cuando la semana cruza de mes
const formatWeekRange = (days: Date[]): string => {
  const first = days[0];
  const last  = days[6];
  const month = (d: Date) => d.toLocaleDateString('es-CL', { month: 'short' }).replace('.', '');
  return first.getMonth() === last.getMonth()
    ? `${first.getDate()} – ${last.getDate()} ${month(last)}`
    : `${first.getDate()} ${month(first)} – ${last.getDate()} ${month(last)}`;
};

// Síntesis "consultas del día" (cal-day-summary) — el color queda como acento
// decorativo (borde izquierdo de la fila), nunca como color de texto:
// --apple-green/--apple-orange no cumplen 3:1 contra blanco, así que el
// estado siempre se confirma con texto plano (--text-secondary).
const DAY_SUMMARY_STATUS_META: Record<string, { color: string; label: string }> = {
  agendada:    { color: 'var(--apple-orange)', label: 'Pendiente' },
  programada:  { color: 'var(--apple-orange)', label: 'Pendiente' },
  confirmada:  { color: 'var(--apple-green)',  label: 'Confirmada' },
  en_progreso: { color: 'var(--apple-blue)',   label: 'En curso' },
  completada:  { color: 'var(--apple-green)',  label: 'Completada' },
  no_asistio:  { color: 'var(--apple-red)',    label: 'No asistió' },
};

const getDaySummaryStatusMeta = (status: string) =>
  DAY_SUMMARY_STATUS_META[status] ?? { color: 'var(--text-muted)', label: status };

const DAY_SUMMARY_MODALIDAD_META: Record<string, { icon: string; label: string }> = {
  presencial: { icon: 'fa-building', label: 'Presencial' },
  online:     { icon: 'fa-video',    label: 'Online' },
  domicilio:  { icon: 'fa-house',    label: 'Domicilio' },
};

const getDaySummaryModalidadMeta = (modality: string) =>
  DAY_SUMMARY_MODALIDAD_META[modality] ?? { icon: 'fa-circle-question', label: modality };

const TherapistCalendar: React.FC<TherapistCalendarProps> = ({ therapist }) => {
  const [currentView, setCurrentView] = useState<CalendarView>(
    () => window.innerWidth < 768 ? 'day' : 'week'
  );
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [showAppointmentScheduler, setShowAppointmentScheduler] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{date: string, time: string} | null>(null);
  const [isMobileView, setIsMobileView] = useState(() => window.innerWidth < 768);
  const [monthDaySheetDate, setMonthDaySheetDate] = useState<string | null>(null);
  const [clinicOccupiedRaw, setClinicOccupiedRaw] = useState<Record<string, { start: string; end: string }[]>>({});
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportingPeriod, setExportingPeriod] = useState<AgendaPeriod | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // Contenedor con scroll vertical de la vista activa (Día / Semana). Al entrar
  // se posiciona cerca de la hora actual, no en START_HOUR (09:00) — ver el
  // useLayoutEffect de más abajo.
  const gridScrollRef = useRef<HTMLDivElement>(null);
  // Reloj para la línea "ahora" — refresca cada minuto
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  // Evita que una respuesta vieja (día anterior, cambiado rápido) pise el
  // resultado de la última petición — sin esto el resumen del día queda
  // desactualizado tras navegar rápido entre fechas.
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (therapist.id) {
      loadRealAppointments();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [therapist.id, selectedDate]);

  // Bloques ocupados de la clínica (consultorio compartido) — solo aplica a día/semana,
  // donde hay grid horario. La vista mes no tiene granularidad de horas.
  useEffect(() => {
    if (!therapist.id || currentView === 'month') return;
    const dates = currentView === 'week'
      ? getWeekDays(selectedDate).map(toLocalDateStr)
      : [toLocalDateStr(selectedDate)];
    dates.forEach(loadClinicOccupiedSlots);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [therapist.id, selectedDate, currentView]);

  // prevWidthRef evita que iOS Safari dispare el handler al hacer scroll
  const prevWidthRef = useRef(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      if (newWidth === prevWidthRef.current) return;
      prevWidthRef.current = newWidth;
      // Semana no se fuerza a Día en mobile: tiene su propia vista
      // (chips de días + día expandido, renderMobileWeekView)
      setIsMobileView(newWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!showExportMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  useEffect(() => {
    if (!therapist.id) return;

    const channel = supabase
      .channel(`consultas:terapeuta_id=eq.${therapist.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'consultas',
          filter: `terapeuta_id=eq.${therapist.id}`
        },
        () => {
          loadRealAppointments();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [therapist.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const getAppointmentsForDate = (date: string) => {
    return appointments.filter(apt => apt.date === date && apt.status !== 'cancelada');
  };

  const getWeekDays = (date: Date) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1);
    start.setDate(diff);

    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const currentDay = new Date(start);
      currentDay.setDate(start.getDate() + i);
      weekDays.push(currentDay);
    }
    return weekDays;
  };

  // Días calendario del mes de `date` (1 al último día) — a diferencia de la
  // grilla de la vista "mes" (que rellena semanas completas con días de meses
  // vecinos para armar el grid), la agenda exportada solo debe listar el mes
  // pedido: los días de relleno no son parte de "la agenda de agosto".
  const getMonthDays = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: lastDay }, (_, i) => new Date(year, month, i + 1));
  };

  async function handleExportAgenda(period: AgendaPeriod) {
    setShowExportMenu(false);
    setExportError(null);

    const days = period === 'day' ? [selectedDate] : period === 'week' ? getWeekDays(selectedDate) : getMonthDays(selectedDate);
    const dayStrs = new Set(days.map(toLocalDateStr));
    const periodAppointments = appointments.filter(apt => dayStrs.has(apt.date));

    // toLocaleDateString('es-CL', {weekday:'long', ...}) devuelve el día en
    // minúscula ("miércoles, 26...") — se capitaliza para que combine con los
    // subtítulos de fecha de la tabla (formatFullDate en agendaBlocks.ts, que
    // sí capitaliza).
    const periodLabel = period === 'day'
      ? (() => {
          const label = selectedDate.toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
          return label.charAt(0).toUpperCase() + label.slice(1);
        })()
      : period === 'week'
        ? `Semana del ${days[0].getDate()} al ${days[6].getDate()} de ${days[6].toLocaleDateString('es-CL', { month: 'long', year: 'numeric' })}`
        : selectedDate.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });

    // Ventana abierta ANTES de cualquier await — mismo motivo que
    // ClinicalRecords.tsx (iOS Safari/popup blockers: window.open() solo pasa
    // si corre sincrónico dentro del gesto de click).
    const win = window.open('', '_blank');
    if (!win) {
      setExportError('Habilitá las ventanas emergentes para exportar el PDF.');
      return;
    }
    setExportingPeriod(period);
    try {
      const { exportAgendaPdf } = await import('../../lib/agendaExport');
      await exportAgendaPdf({
        therapist: {
          nombre: therapist.nombre ?? therapist.name,
          apellido: therapist.apellido ?? '',
          especialidad: therapist.especialidad,
          titulo: therapist.titulo,
          rut: therapist.rut,
          telefono: therapist.telefono,
          email: therapist.email,
        },
        appointments: periodAppointments,
        period,
        periodLabel,
        win,
      });
    } catch {
      win.close();
      setExportError('No se pudo generar el PDF. Intentá nuevamente.');
    } finally {
      setExportingPeriod(null);
    }
  }

  // Posición de apertura del grid: 1 h antes de "ahora" si el período incluye
  // hoy; si no, 30 min antes de la primera cita. scrollTop directo sobre el
  // contenedor (antes scrollIntoView de un ancla, que además arrastraba el
  // scroll de la página). 'loading' en las deps: mientras es true se renderiza
  // el skeleton y el contenedor no existe.
  useLayoutEffect(() => {
    const el = gridScrollRef.current;
    if (loading || !el || currentView === 'month') return;

    const visibleDates = currentView === 'week' && !isMobileView
      ? getWeekDays(selectedDate).map(toLocalDateStr)
      : [toLocalDateStr(selectedDate)];

    let targetMin: number | null = null;
    if (visibleDates.includes(toLocalDateStr(new Date()))) {
      const n = new Date();
      targetMin = n.getHours() * 60 + n.getMinutes() - 60;
    } else {
      const starts = visibleDates
        .flatMap(d => getAppointmentsForDate(d))
        .map(a => hhmmToMinutes(a.start_time));
      if (starts.length > 0) targetMin = Math.min(...starts) - 30;
    }

    el.scrollTop = targetMin === null ? 0 : Math.max(0, (targetMin - START_HOUR * 60) * PPM);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, selectedDate, isMobileView, loading]);

  // Color según tipo de terapia — WCAG AA (4.5:1 sobre blanco) — sync con CLAUDE.md
  const getTherapyColor = (type: string) => {
    const normalized = type?.toLowerCase() ?? '';
    if (normalized.includes('individual'))                               return '#1A6FBF';
    if (normalized.includes('pareja') || normalized.includes('parejas')) return '#C05E00';
    if (normalized.includes('famil'))                                    return '#057A55';
    if (normalized.includes('sexo'))                                     return '#6B21A8';
    return '#1A6FBF';
  };

  // Posición temporal matemáticamente exacta — misma fórmula que las líneas del grid
  const getEventPosition = (startTime: string, durationMin: number) => {
    const [h, m] = startTime.split(':').map(Number);
    const top    = (h * 60 + m - START_HOUR * 60) * PPM;
    const height = Math.max(durationMin * PPM, 24);
    return { top, height };
  };

  const hhmmToMinutes = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };

  // Bloques donde el consultorio físico compartido está lleno (get_clinic_blocked_slots_for_date,
  // sin PII ni terapeuta_id — cuenta capacidad, no identidad). Se excluyen los tramos que coinciden
  // con una cita propia del terapeuta (ya se ven como su propio evento) para no duplicar.
  const getOccupiedRangesForDate = (dateStr: string) => {
    const raw = clinicOccupiedRaw[dateStr] || [];
    const ownAppts = getAppointmentsForDate(dateStr);

    const free = raw.filter(slot => {
      const slotStart = hhmmToMinutes(slot.start);
      const slotEnd   = hhmmToMinutes(slot.end);
      return !ownAppts.some(apt => {
        const aptStart = hhmmToMinutes(apt.start_time);
        const aptEnd   = aptStart + apt.duration;
        return slotStart < aptEnd && slotEnd > aptStart;
      });
    });

    const merged: { start: string; end: string }[] = [];
    for (const slot of free) {
      const last = merged[merged.length - 1];
      if (last && last.end === slot.start) {
        last.end = slot.end;
      } else {
        merged.push({ ...slot });
      }
    }
    return merged;
  };

  // Bloques "consultorio ocupado" — informativos, no clicables (pointer-events: none en CSS)
  // para que el click en el grid siga abriendo el agendador normalmente.
  const renderOccupiedBlocks = (dateStr: string) => {
    return getOccupiedRangesForDate(dateStr).map((range, i) => {
      const durationMin = hhmmToMinutes(range.end) - hhmmToMinutes(range.start);
      const { top, height } = getEventPosition(range.start, durationMin);
      return (
        <div key={`occ-${dateStr}-${i}`} className="cal-occupied-block" style={{ top, height }}>
          {height >= 40 && <span className="cal-occupied-label">🔒 Bloque ocupado</span>}
        </div>
      );
    });
  };

  // ── Piezas comunes del grid temporal (Día, Semana, Semana mobile) ─────────
  const renderTimeGutter = (sticky: boolean) => (
    <div className={`cal-time-gutter${sticky ? ' cal-gutter-sticky' : ''}`} style={{ height: GRID_HEIGHT }}>
      {HOURS.map(h => (
        <div
          key={h}
          className="cal-time-label"
          style={{
            top: (h - START_HOUR) * ROW_H,
            ...(h === START_HOUR ? { transform: 'none', paddingTop: 2 } : {})
          }}
        >
          {String(h).padStart(2, '0')}:00
        </div>
      ))}
    </div>
  );

  const renderHourLines = () => (
    <>
      {HOURS.map(h => (
        <div key={h} className="cal-hline cal-hline--hour" style={{ top: (h - START_HOUR) * ROW_H }} />
      ))}
      {HOURS.slice(0, -1).map(h => (
        <div key={`hh${h}`} className="cal-hline cal-hline--half" style={{ top: (h - START_HOUR) * ROW_H + ROW_H / 2 }} />
      ))}
    </>
  );

  // Línea roja de hora actual — solo en la columna de hoy y dentro del rango
  const renderNowLine = (dateStr: string) => {
    if (dateStr !== toLocalDateStr(now)) return null;
    const minutes = now.getHours() * 60 + now.getMinutes();
    if (minutes < START_HOUR * 60 || minutes > (END_HOUR + 1) * 60) return null;
    return (
      <div className="cal-now-line" style={{ top: (minutes - START_HOUR * 60) * PPM }} aria-hidden="true" />
    );
  };

  // Bloque de cita — fondo tintado + barra de acento del color de terapia
  // (estilos scoped a .tc-calendar en TherapistCalendar.css). Presencial es el
  // default → sin ícono; online/domicilio sí. ✓ solo en confirmada.
  const renderEvent = (appointment: Appointment, shortName: boolean) => {
    const { top, height } = getEventPosition(appointment.start_time, appointment.duration);
    const modality = getDaySummaryModalidadMeta(appointment.modality);
    const name     = shortName ? appointment.patient_name.split(' ')[0] : appointment.patient_name;
    const tight    = height < TIGHT_EVENT_H;

    return (
      <button
        type="button"
        key={appointment.id}
        className={`cal-event ${appointment.status}${tight ? ' cal-event--tight' : ''}`}
        style={{ top, height, '--evt-color': getTherapyColor(appointment.therapy_type) } as React.CSSProperties}
        title={`${appointment.start_time}–${appointment.end_time} · ${appointment.patient_name} · ${modality.label}`}
        onClick={(e) => { e.stopPropagation(); setSelectedAppointment(appointment); }}
      >
        <span className="cal-evt-name">
          {tight && <span className="cal-evt-time">{appointment.start_time}</span>}
          <span className="cal-evt-name-text">{name}</span>
        </span>
        {!tight && (
          <span className="cal-evt-meta">
            <span className="cal-evt-time">{appointment.start_time}–{appointment.end_time}</span>
            {appointment.modality !== 'presencial' && (
              <i className={`fas ${modality.icon}`} aria-label={modality.label} />
            )}
            {appointment.status === 'confirmada' && <i className="fas fa-check" aria-label="Confirmada" />}
          </span>
        )}
      </button>
    );
  };

  // Grid de un día — vista Día y día expandido de Semana mobile.
  // El título del día no se repite acá: ya está en el header del calendario
  // y en la síntesis "Consultas de…" de arriba.
  const renderDayGrid = (dateStr: string) => {
    const dayAppointments = getAppointmentsForDate(dateStr);

    return (
      <div className="cal-scroll-wrap" ref={gridScrollRef}>
        <div className="cal-time-grid">
          {renderTimeGutter(false)}
          <div
            className="cal-events-area"
            style={{ height: GRID_HEIGHT }}
            onClick={(e) => handleGridClick(e, dateStr)}
          >
            {renderHourLines()}
            {/* Bloques de consultorio ocupado por otros terapeutas */}
            {renderOccupiedBlocks(dateStr)}
            {renderNowLine(dateStr)}
            {dayAppointments.map(appointment => renderEvent(appointment, false))}
          </div>
        </div>
      </div>
    );
  };

  // Renderizar vista de día
  const renderDayView = () => (
    <div className="cal-view">
      {renderDayGrid(toLocalDateStr(selectedDate))}
    </div>
  );

  // Vista semanal mobile — 7 chips a ancho completo (con nº de citas) + un día expandido
  const renderMobileWeekView = () => {
    const weekDays = getWeekDays(selectedDate);
    const todayStr = toLocalDateStr(now);
    const activeDateStr = toLocalDateStr(selectedDate);

    return (
      <div className="cal-view cal-mobile-week">
        <div className="cal-mobile-chips-scroll" role="tablist" aria-label="Días de la semana">
          {weekDays.map((day, i) => {
            const dStr = toLocalDateStr(day);
            const isActive = dStr === activeDateStr;
            const isToday = dStr === todayStr;
            const count = getAppointmentsForDate(dStr).length;
            return (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`cal-week-chip${isActive ? ' active' : ''}${isToday ? ' today' : ''}`}
                onClick={() => setSelectedDate(day)}
              >
                <span className="cal-chip-day-name">
                  {day.toLocaleDateString('es-CL', { weekday: 'narrow' })}
                </span>
                <span className="cal-chip-day-num">{day.getDate()}</span>
                <span className="cal-chip-count" aria-label={`${count} citas`}>{count > 0 ? count : ''}</span>
              </button>
            );
          })}
        </div>

        {renderDayGrid(activeDateStr)}
      </div>
    );
  };

  // Renderizar vista de semana — un solo contenedor de scroll (vertical +
  // horizontal): header de días sticky arriba, gutter de horas sticky a la izquierda.
  const renderWeekView = () => {
    const weekDays = getWeekDays(selectedDate);
    const todayStr  = toLocalDateStr(now);

    return (
      <div className="cal-view cal-week-view" ref={gridScrollRef}>
        <div className="cal-week-inner">
          <div className="cal-week-header">
            <div className="cal-week-gutter-spacer" />
            {weekDays.map((day, i) => {
              const dStr    = toLocalDateStr(day);
              const isToday = dStr === todayStr;
              const count   = getAppointmentsForDate(dStr).length;
              return (
                <button
                  key={i}
                  type="button"
                  className={`cal-week-day-hdr${isToday ? ' today' : ''}`}
                  onClick={() => { setSelectedDate(day); setCurrentView('day'); }}
                  title="Ver día"
                >
                  <span className="cal-week-day-name">
                    {day.toLocaleDateString('es-CL', { weekday: 'short' }).replace('.', '')}
                  </span>
                  <span className="cal-week-day-num">{day.getDate()}</span>
                  {count > 0 && <span className="cal-week-day-count">{count}</span>}
                </button>
              );
            })}
          </div>

          <div className="cal-time-grid cal-week-grid">
            {renderTimeGutter(true)}

            {weekDays.map((day, dayIndex) => {
              const dateStr  = toLocalDateStr(day);
              const isToday  = dateStr === todayStr;
              const dayAppts = getAppointmentsForDate(dateStr);

              return (
                <div
                  key={dayIndex}
                  className={`cal-day-col${isToday ? ' today' : ''}`}
                  style={{ height: GRID_HEIGHT }}
                  onClick={(e) => handleGridClick(e, dateStr)}
                >
                  {renderHourLines()}
                  {/* Bloques de consultorio ocupado por otros terapeutas */}
                  {renderOccupiedBlocks(dateStr)}
                  {renderNowLine(dateStr)}
                  {dayAppts.map(appointment => renderEvent(appointment, true))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Renderizar vista de mes
  const renderMonthView = () => {
    const firstDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const lastDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
    const daysInMonth = [];

    const firstWeekday = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    for (let i = firstWeekday - 1; i >= 0; i--) {
      const day = new Date(firstDay);
      day.setDate(day.getDate() - i - 1);
      daysInMonth.push({ date: day, isCurrentMonth: false });
    }

    for (let day = 1; day <= lastDay.getDate(); day++) {
      daysInMonth.push({
        date: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day),
        isCurrentMonth: true
      });
    }

    // 5 filas cuando el mes cabe (antes siempre 6 → una semana extra del mes siguiente)
    const remainingDays = Math.ceil(daysInMonth.length / 7) * 7 - daysInMonth.length;
    for (let day = 1; day <= remainingDays; day++) {
      const nextMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, day);
      daysInMonth.push({ date: nextMonth, isCurrentMonth: false });
    }

    return (
      <div className="calendar-month-view">
        <div className="month-header">
          <div className="weekday-labels">
            {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(day => (
              <div key={day} className="weekday-label">{day}</div>
            ))}
          </div>
        </div>

        <div className="month-grid">
          {daysInMonth.map((dayInfo, index) => {
            const dateStr = toLocalDateStr(dayInfo.date);
            const dayAppointments = getAppointmentsForDate(dateStr);
            const isToday = dateStr === toLocalDateStr(now);

            return (
              <div
                key={index}
                className={`month-day ${!dayInfo.isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${isMobileView ? 'month-day--mobile' : ''}`}
                onClick={() => {
                  if (isMobileView) {
                    setMonthDaySheetDate(dateStr);
                  } else {
                    // Antes solo cambiaba la fecha y la vista Mes quedaba igual
                    // (click sin efecto visible) → abre el día
                    setSelectedDate(dayInfo.date);
                    setCurrentView('day');
                  }
                }}
              >
                <div className="day-number">{dayInfo.date.getDate()}</div>
                {isMobileView ? (
                  <div className="day-dots">
                    {dayAppointments.slice(0, 4).map(apt => (
                      <span
                        key={apt.id}
                        className="month-dot"
                        style={{ backgroundColor: getTherapyColor(apt.therapy_type) }}
                      />
                    ))}
                    {dayAppointments.length > 4 && (
                      <span className="month-dot-overflow">+{dayAppointments.length - 4}</span>
                    )}
                  </div>
                ) : (
                  <div className="day-appointments">
                    {dayAppointments.slice(0, 3).map(apt => (
                      <button
                        type="button"
                        key={apt.id}
                        className={`appointment-indicator tc-month-chip ${apt.status}`}
                        style={{ '--evt-color': getTherapyColor(apt.therapy_type) } as React.CSSProperties}
                        title={`${apt.start_time} · ${apt.patient_name} · ${getDaySummaryModalidadMeta(apt.modality).label}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAppointment(apt);
                        }}
                      >
                        <span className="apt-indicator-time">{apt.start_time}</span>
                        <span className="apt-indicator-name">{apt.patient_name.split(' ')[0]}</span>
                        {apt.modality !== 'presencial' && (
                          <i className={`fas ${getDaySummaryModalidadMeta(apt.modality).icon} tc-month-chip__icon`} aria-hidden="true" />
                        )}
                      </button>
                    ))}
                    {dayAppointments.length > 3 && (
                      <div className="more-appointments">+{dayAppointments.length - 3} más</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Navegar fechas
  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);

    switch (currentView) {
      case 'day':
        newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
        break;
      case 'week':
        newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
        break;
    }

    setSelectedDate(newDate);
  };

  const handleNewAppointment = (date?: string, time?: string) => {
    if (date && time) {
      setSelectedTimeSlot({ date, time });
    } else {
      setSelectedTimeSlot(null);
    }
    setShowAppointmentScheduler(true);
  };

  const handleAppointmentCreated = (_newAppointment: any) => {
    setShowAppointmentScheduler(false);
    setSelectedTimeSlot(null);
    loadRealAppointments();
  };

  // Click en área vacía del grid → calcular hora y abrir agendador
  const handleGridClick = (e: React.MouseEvent<HTMLDivElement>, dateStr: string) => {
    if ((e.target as HTMLElement).closest('.cal-event')) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y    = e.clientY - rect.top;
    const minutesFromStart = Math.floor(y / PPM);
    const totalMinutes     = START_HOUR * 60 + minutesFromStart;
    const rounded          = Math.round(totalMinutes / 30) * 30;
    const hh               = Math.min(Math.floor(rounded / 60), END_HOUR - 1);
    const mm               = rounded % 60;
    handleNewAppointment(dateStr, `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`);
  };

  const loadRealAppointments = async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);

    try {
      const { data: consultas, error } = await supabase
        .from('consultas')
        .select(`
          id, fecha_hora, duracion, modalidad, estado, tipo_consulta,
          valor_consulta, notas_terapeuta, meet_link, terapeuta_id,
          metodo_pago_declarado,
          pacientes(nombre, apellido, email, telefono)
        `)
        .eq('terapeuta_id', therapist.id)
        .gte('fecha_hora', new Date(selectedDate.getTime() - 60 * 24 * 60 * 60 * 1000).toISOString())
        .lte('fecha_hora', new Date(selectedDate.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString())
        .order('fecha_hora', { ascending: true });

      if (error) throw error;

      const pad2 = (n: number) => String(n).padStart(2, '0');

      const transformedAppointments: Appointment[] = (consultas || []).map((consulta: any) => {
        const fechaHora = new Date(consulta.fecha_hora);
        const endTime   = new Date(fechaHora.getTime() + (consulta.duracion || 50) * 60000);

        let patientName  = 'Paciente sin nombre';
        let patientEmail: string | undefined;
        let patientPhone: string | undefined;
        if (consulta.pacientes) {
          const patient = Array.isArray(consulta.pacientes)
            ? consulta.pacientes[0]
            : consulta.pacientes;
          if (patient) {
            patientName  = `${patient.nombre || ''} ${patient.apellido || ''}`.trim() || 'Paciente sin nombre';
            patientEmail = patient.email;
            patientPhone = patient.telefono;
          }
        }

        const rawType: string = (consulta as any).tipo_consulta || '';
        let therapyType: 'individual' | 'pareja' | 'familia' = 'individual';
        if (rawType.toLowerCase().includes('pareja') || rawType === 'pareja') {
          therapyType = 'pareja';
        } else if (rawType.toLowerCase().includes('familiar') || rawType.toLowerCase().includes('familia') || rawType === 'familia') {
          therapyType = 'familia';
        }

        // Use explicit America/Santiago timezone — avoids wrong day/hour if browser TZ differs
        return {
          id: consulta.id,
          patient_name:  patientName,
          patient_email: patientEmail,
          patient_phone: patientPhone,
          therapy_type: therapyType,
          start_time: toSantiagoTime(fechaHora),
          end_time:   toSantiagoTime(endTime),
          date:       toSantiagoDateStr(fechaHora),
          status:     (consulta.estado as Appointment['status']) || 'programada',
          modality:   consulta.modalidad || 'presencial',
          duration:   consulta.duracion || 50,
          notes:      (consulta as any).notas_terapeuta,
          precio:     (consulta as any).valor_consulta,
          meet_link:  (consulta as any).meet_link ?? undefined,
          paymentMethod: ((consulta as any).metodo_pago_declarado as 'efectivo' | 'transferencia' | 'tarjeta') || 'efectivo',
        };
      });

      if (requestId !== requestIdRef.current) return; // llegó una petición más nueva primero, se descarta esta
      setAppointments(transformedAppointments);
    } catch (error) {
      if (requestId === requestIdRef.current) console.error('Error al cargar citas:', error);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  // Trae los bloques donde el consultorio físico compartido está a plena capacidad
  // (RPC sin PII, ya usada por AppointmentScheduler) y los guarda en hora local Santiago.
  const loadClinicOccupiedSlots = async (dateStr: string) => {
    try {
      const { data, error } = await supabase.rpc('get_clinic_blocked_slots_for_date', {
        p_date: dateStr,
        p_slot_duration: 30
      });

      if (error || !data) return;

      const slots = (data as { slot_start: string; slot_end: string }[])
        .map(row => ({
          start: toSantiagoTime(new Date(row.slot_start)),
          end:   toSantiagoTime(new Date(row.slot_end)),
        }))
        .sort((a, b) => a.start.localeCompare(b.start));

      setClinicOccupiedRaw(prev => ({ ...prev, [dateStr]: slots }));
    } catch (error) {
      console.error('Error al cargar bloques ocupados de clínica:', error);
    }
  };

  if (loading) {
    return (
      <div className="therapist-calendar" role="status" aria-label="Cargando la agenda">
        <div className="cal-skeleton">
          <div className="cal-skeleton__toolbar">
            <Skeleton width={190} height={22} />
            <Skeleton width={230} height={34} radius={10} />
          </div>
          <div className="cal-skeleton__legend">
            <Skeleton width={90} height={14} radius="pill" />
            <Skeleton width={90} height={14} radius="pill" />
            <Skeleton width={90} height={14} radius="pill" />
          </div>
          <Skeleton height={110} radius={12} />
          <Skeleton height={360} radius={12} />
        </div>
      </div>
    );
  }

  return (
    <div className="therapist-calendar tc-calendar">
      <div className="calendar-header">
        <div className="calendar-navigation">
          <button className="nav-btn" onClick={() => navigateDate('prev')} aria-label="Período anterior">
            <i className="fas fa-chevron-left"></i>
          </button>
          <div className="current-period">
            {currentView === 'month' && (
              <h2>{selectedDate.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' })}</h2>
            )}
            {currentView === 'week' && (
              <h2>{formatWeekRange(getWeekDays(selectedDate))}</h2>
            )}
            {currentView === 'day' && (
              <h2>
                <span className="period-long">{selectedDate.toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
                <span className="period-short">{selectedDate.toLocaleDateString('es-CL', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
              </h2>
            )}
          </div>
          <button className="nav-btn" onClick={() => navigateDate('next')} aria-label="Período siguiente">
            <i className="fas fa-chevron-right"></i>
          </button>
          <button className="today-btn" onClick={() => setSelectedDate(new Date())}>
            Hoy
          </button>
        </div>

        <div className="calendar-controls">
          <div className="view-switcher">
            {(['day', 'week', 'month'] as CalendarView[]).map(view => (
              <button
                key={view}
                className={`view-btn ${currentView === view ? 'active' : ''}`}
                onClick={() => setCurrentView(view)}
              >
                {view === 'day' ? 'Día' : view === 'week' ? 'Semana' : 'Mes'}
              </button>
            ))}
          </div>

          <div className="calendar-actions">
            <button className="action-btn" onClick={() => handleNewAppointment()}>
              <i className="fas fa-plus"></i>
              Agendar Cita
            </button>
            <div className="export-menu-wrapper" ref={exportMenuRef}>
              <button
                className="action-btn secondary"
                onClick={() => setShowExportMenu(v => !v)}
                disabled={exportingPeriod !== null}
              >
                <i className={`fas ${exportingPeriod ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
                Exportar
              </button>
              {showExportMenu && (
                <div className="export-menu">
                  <button className="export-menu-item" onClick={() => handleExportAgenda('day')}>
                    <i className="fas fa-calendar-day"></i>
                    Agenda del día
                  </button>
                  <button className="export-menu-item" onClick={() => handleExportAgenda('week')}>
                    <i className="fas fa-calendar-week"></i>
                    Agenda de la semana
                  </button>
                  <button className="export-menu-item" onClick={() => handleExportAgenda('month')}>
                    <i className="fas fa-calendar"></i>
                    Agenda del mes
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        {exportError && <div className="export-error">{exportError}</div>}
      </div>

      <div className="calendar-legend">
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: '#1A6FBF' }}></div>
          <span>Individual</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: '#C05E00' }}></div>
          <span>Parejas</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: '#057A55' }}></div>
          <span>Familia</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: '#6B21A8' }}></div>
          <span>Sexología</span>
        </div>
        <span className="tc-legend-sep" aria-hidden="true" />
        <div className="legend-item"><i className="fas fa-video" aria-hidden="true" /><span>Online</span></div>
        <div className="legend-item"><i className="fas fa-house" aria-hidden="true" /><span>Domicilio</span></div>
        <div className="legend-item"><i className="fas fa-check" aria-hidden="true" /><span>Confirmada</span></div>
      </div>

      {currentView !== 'month' && (() => {
        const dayAppointments = getAppointmentsForDate(toLocalDateStr(selectedDate));
        const isToday = toLocalDateStr(selectedDate) === toLocalDateStr(new Date());
        const dayLabel = isToday
          ? 'Consultas de hoy'
          : `Consultas del ${selectedDate.toLocaleDateString('es-CL', { day: 'numeric', month: 'long' })}`;
        return (
          <div className="cal-day-summary">
            <div className="cal-day-summary__header">
              <h3 className="cal-day-summary__title">
                <i className="fas fa-calendar-day" aria-hidden="true" />
                {dayLabel}
              </h3>
              {dayAppointments.length > 0 && (
                <span className="cal-day-summary__count">
                  {dayAppointments.length} {dayAppointments.length === 1 ? 'consulta' : 'consultas'}
                </span>
              )}
            </div>

            {dayAppointments.length === 0 ? (
              <div className="cal-day-summary__empty">
                <i className="fas fa-mug-hot" aria-hidden="true" />
                <p>Sin consultas agendadas para este día</p>
              </div>
            ) : (
              <ul className="cal-day-summary__list" role="list">
                {dayAppointments.map(appointment => {
                  const statusMeta = getDaySummaryStatusMeta(appointment.status);
                  const modalidadMeta = getDaySummaryModalidadMeta(appointment.modality);
                  return (
                    <li
                      key={appointment.id}
                      className="cal-day-summary-row"
                      style={{ '--row-status-color': statusMeta.color } as React.CSSProperties}
                    >
                      <span className="cal-day-summary-row__time">{appointment.start_time}</span>
                      <span className="cal-day-summary-row__patient" title={appointment.patient_name}>
                        {appointment.patient_name}
                      </span>
                      <span className="cal-day-summary-row__modalidad">
                        <i className={`fas ${modalidadMeta.icon}`} aria-hidden="true" />
                        {modalidadMeta.label}
                      </span>
                      <span className="cal-day-summary-row__separator" aria-hidden="true">&middot;</span>
                      <span className="cal-day-summary-row__status">{statusMeta.label}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })()}

      <div className="calendar-content">
        {currentView === 'day'   && renderDayView()}
        {currentView === 'week'  && isMobileView  && renderMobileWeekView()}
        {currentView === 'week'  && !isMobileView && renderWeekView()}
        {currentView === 'month' && renderMonthView()}
      </div>

      {selectedAppointment && (
        <AppointmentModal
          appointment={selectedAppointment}
          therapistId={therapist.id}
          therapistName={therapist.name}
          therapistEmail={therapist.email ?? 'angeldiazdiaz01@gmail.com'}
          onClose={() => setSelectedAppointment(null)}
          onUpdate={() => {
            setSelectedAppointment(null);
            loadRealAppointments();
          }}
          onStatusChange={(newStatus) => {
            const id = selectedAppointment.id;
            setAppointments(prev => prev.map(a => a.id === id ? { ...a, status: newStatus } : a));
            setSelectedAppointment(prev => (prev && prev.id === id) ? { ...prev, status: newStatus } : prev);
          }}
        />
      )}

      {showAppointmentScheduler && (
        <AppointmentScheduler
          onAppointmentCreated={handleAppointmentCreated}
          onCancel={() => setShowAppointmentScheduler(false)}
          therapistId={therapist.id}
          therapistName={therapist.name}
          therapistEmail={therapist.email}
          selectedDate={selectedTimeSlot?.date}
          selectedTime={selectedTimeSlot?.time}
        />
      )}

      {monthDaySheetDate && (
        <MonthDaySheet
          dateStr={monthDaySheetDate}
          appointments={getAppointmentsForDate(monthDaySheetDate)}
          onClose={() => setMonthDaySheetDate(null)}
          onSelectAppointment={(apt) => { setMonthDaySheetDate(null); setSelectedAppointment(apt); }}
        />
      )}
    </div>
  );
};

// Bottom sheet — detalle del día desde vista mensual mobile
const MonthDaySheet: React.FC<{
  dateStr: string;
  appointments: Appointment[];
  onClose: () => void;
  onSelectAppointment: (apt: Appointment) => void;
}> = ({ dateStr, appointments, onClose, onSelectAppointment }) => {
  const date = new Date(dateStr + 'T12:00:00');
  const label = date.toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="day-sheet-overlay" onClick={onClose}>
      <div className="day-sheet" onClick={e => e.stopPropagation()}>
        <div className="day-sheet-handle" />
        <div className="day-sheet-header">
          <h3 className="day-sheet-title">{label}</h3>
          <button className="day-sheet-close" onClick={onClose}>
            <i className="fas fa-times" />
          </button>
        </div>
        <div className="day-sheet-list">
          {appointments.length === 0 ? (
            <p className="day-sheet-empty">Sin citas este día</p>
          ) : (
            appointments.map(apt => (
              <div
                key={apt.id}
                className="day-sheet-item"
                onClick={() => onSelectAppointment(apt)}
              >
                <span className="day-sheet-dot" style={{ backgroundColor: getTherapyColor(apt.therapy_type) }} />
                <span className="day-sheet-time">{apt.start_time}</span>
                <div className="day-sheet-patient">
                  <div className="day-sheet-patient-name">{apt.patient_name}</div>
                  <div className="day-sheet-patient-type">{apt.therapy_type}</div>
                </div>
                <span className="day-sheet-modality" title={getDaySummaryModalidadMeta(apt.modality).label}>
                  <i className={`fas ${getDaySummaryModalidadMeta(apt.modality).icon}`} aria-label={getDaySummaryModalidadMeta(apt.modality).label} />
                </span>
                <span
                  className="day-sheet-payment"
                  title={paymentMethodLabel(apt.paymentMethod)}
                  aria-label={paymentMethodLabel(apt.paymentMethod)}
                >
                  {apt.paymentMethod === 'transferencia' ? '🏦' : apt.paymentMethod === 'tarjeta' ? '💳' : '💵'}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// Modal para mostrar detalles de la cita
const AppointmentModal: React.FC<{
  appointment: Appointment;
  therapistId: string;
  therapistName: string;
  therapistEmail: string;
  onClose: () => void;
  onUpdate: () => void;
  onStatusChange: (newStatus: Appointment['status']) => void;
}> = ({ appointment, therapistId, therapistName, therapistEmail, onClose, onUpdate, onStatusChange }) => {
  const [showReschedule, setShowReschedule] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();
  const {
    actionLoading,
    actionError,
    clearActionError,
    advanceStatus,
    cancelConsultation,
    rescheduleConsultation,
    deleteConsultation,
  } = useConsultationActions();

  const canChangeStatus = appointment.status !== 'completada' && appointment.status !== 'cancelada';
  const canCancel       = appointment.status !== 'completada' && appointment.status !== 'cancelada';
  const canDelete       = appointment.status !== 'completada';

  const handleDeleteAppointment = async () => {
    const confirmed = await confirm({
      title: '¿Eliminar esta consulta permanentemente?',
      message: `Paciente: ${appointment.patient_name}\nFecha: ${appointment.date} · ${appointment.start_time}\n\nEsta acción no se puede deshacer.`,
      confirmLabel: 'Eliminar',
      tone: 'danger',
    });
    if (!confirmed) return;

    const ok = await deleteConsultation(appointment.id);
    if (ok) {
      onUpdate();
    } else {
      toast.error('No se pudo eliminar la consulta. Intentá nuevamente.');
    }
  };

  const handleCancelAppointment = async () => {
    const confirmed = await confirm({
      title: '¿Cancelar esta cita?',
      message: `Paciente: ${appointment.patient_name}\nFecha: ${appointment.date} · ${appointment.start_time}`,
      confirmLabel: 'Cancelar cita',
      cancelLabel: 'Volver',
      tone: 'danger',
    });
    if (!confirmed) return;

    const ok = await cancelConsultation(appointment.id);
    if (!ok) {
      toast.error('No se pudo cancelar la cita. Intentá nuevamente.');
      return;
    }

    if (appointment.patient_email) {
      await EmailService.sendCancellationNotification({
        patientName:    appointment.patient_name,
        patientEmail:   appointment.patient_email,
        therapistName,
        therapistEmail,
        date:           appointment.date,
        time:           appointment.start_time,
        modality:       appointment.modality,
      });
    }

    onUpdate();
  };

  const handleChangeStatus = async () => {
    if (!canChangeStatus) return;

    const newStatus = await advanceStatus(appointment.id);
    if (newStatus) {
      onStatusChange(newStatus as Appointment['status']);
    } else {
      toast.error('No se pudo actualizar el estado. Intentá nuevamente.');
    }
  };

  const handleReschedule = async (newFechaHoraISO: string) => {
    const oldDate = appointment.date;
    const oldTime = appointment.start_time;
    const ok = await rescheduleConsultation(appointment.id, newFechaHoraISO);
    if (ok) {
      if (appointment.patient_email) {
        const newDT = new Date(newFechaHoraISO);
        await EmailService.sendRescheduleNotification({
          patientName:    appointment.patient_name,
          patientEmail:   appointment.patient_email,
          therapistName,
          therapistEmail,
          oldDate,
          oldTime,
          newDate: toSantiagoDateStr(newDT),
          newTime: toSantiagoTime(newDT),
          modality: appointment.modality,
        });
      }
      setShowReschedule(false);
      onUpdate();
    }
    // Si falló, actionError ya quedó seteado por el hook y se muestra inline en RescheduleForm.
  };

  const statusLabels: Record<string, string> = {
    programada: 'Confirmar cita',
    confirmada: 'Marcar completada',
  };

  return (
    <ModalShell title="Detalles de la Cita" onClose={onClose}>
      <div className="appointment-info">
            <div className="info-row">
              <label>Paciente:</label>
              <span>{appointment.patient_name}</span>
            </div>
            <div className="info-row">
              <label>Tipo de Terapia:</label>
              <span className="therapy-badge" style={{ backgroundColor: getTherapyColor(appointment.therapy_type) }}>
                {appointment.therapy_type.charAt(0).toUpperCase() + appointment.therapy_type.slice(1)}
              </span>
            </div>
            <div className="info-row">
              <label>Fecha y Hora:</label>
              <span>{appointment.date} · {appointment.start_time} - {appointment.end_time}</span>
            </div>
            <div className="info-row">
              <label>Duración:</label>
              <span>{appointment.duration} minutos</span>
            </div>
            <div className="info-row">
              <label>Modalidad:</label>
              <span>{appointment.modality === 'presencial' ? 'Presencial' : appointment.modality === 'domicilio' ? 'Domicilio' : 'Online'}</span>
            </div>
            <div className="info-row">
              <label>Método de pago:</label>
              <span className={`payment-method-badge payment-method-badge--${appointment.paymentMethod}`}>
                {appointment.paymentMethod === 'tarjeta' ? '💳 ' : appointment.paymentMethod === 'transferencia' ? '🏦 ' : ''}{paymentMethodLabel(appointment.paymentMethod)}
              </span>
            </div>
            {appointment.modality === 'online' && appointment.meet_link && (
              <div className="info-row">
                <label>Google Meet:</label>
                <a
                  href={appointment.meet_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="modal-meet-link"
                >
                  <i className="fas fa-video"></i> Abrir sala
                </a>
              </div>
            )}
            <div className="info-row">
              <label>Estado:</label>
              <span className={`status-badge ${appointment.status}`}>
                <i className={`fas ${getStatusIcon(appointment.status)}`}></i>
                {appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
              </span>
            </div>
          </div>

          <WhatsappConfirmButton
            patientFirstName={appointment.patient_name.split(' ')[0]}
            patientPhone={appointment.patient_phone}
            date={appointment.date}
            time={appointment.start_time}
            endTime={appointment.end_time}
            onSent={onClose}
          />

          {showReschedule ? (
            <RescheduleForm
              initialDate={appointment.date}
              initialTime={appointment.start_time}
              therapistId={therapistId}
              duration={appointment.duration}
              loading={actionLoading}
              errorMessage={actionError}
              onConfirm={handleReschedule}
              onCancel={() => { setShowReschedule(false); clearActionError(); }}
            />
          ) : (
            <div className="consult-actions">
              {canChangeStatus && (
                <button
                  className="consult-action-btn consult-action-btn--primary"
                  onClick={handleChangeStatus}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <i className="fas fa-spinner fa-spin"></i>
                  ) : (
                    <i className="fas fa-arrow-right"></i>
                  )}
                  {statusLabels[appointment.status] ?? 'Cambiar Estado'}
                </button>
              )}
              {canCancel && (
                <div className="consult-actions__row">
                  <button
                    className="consult-action-btn consult-action-btn--outline"
                    onClick={() => setShowReschedule(true)}
                    disabled={actionLoading}
                  >
                    <i className="fas fa-calendar-alt"></i>
                    Reagendar
                  </button>
                  <button
                    className="consult-action-btn consult-action-btn--danger-soft"
                    onClick={handleCancelAppointment}
                    disabled={actionLoading}
                  >
                    {actionLoading ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fas fa-times"></i>
                    )}
                    Cancelar cita
                  </button>
                </div>
              )}
              {canDelete && (
                <div className="consult-actions__danger-zone">
                  <button
                    className="consult-action-btn consult-action-btn--danger-strong"
                    onClick={handleDeleteAppointment}
                    disabled={actionLoading}
                    title="Eliminar consulta permanentemente"
                  >
                    {actionLoading ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fas fa-trash"></i>
                    )}
                    Eliminar consulta
                  </button>
                </div>
              )}
              {!canChangeStatus && !canCancel && !canDelete && (
                <p className="modal-status-final">
                  Esta cita ya no puede ser modificada
                </p>
              )}
            </div>
          )}
    </ModalShell>
  );
};

// Auxiliares fuera del componente — usadas por AppointmentModal
const getTherapyColor = (type: string) => {
  switch (type) {
    case 'individual': return '#1A6FBF';
    case 'pareja':     return '#C05E00';
    case 'familia':    return '#057A55';
    default:           return '#3D3D3D';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'agendada':    return 'fa-calendar-plus';
    case 'programada':  return 'fa-clock';
    case 'confirmada':  return 'fa-check';
    case 'en_progreso': return 'fa-play';
    case 'completada':  return 'fa-check-circle';
    case 'cancelada':   return 'fa-times';
    case 'no_asistio':  return 'fa-user-times';
    default:            return 'fa-question';
  }
};

export default TherapistCalendar;
